import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PackageSearch, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { VehiclePicker } from "@/components/VehiclePicker";
import { ImageUploader } from "@/components/ImageUploader";

export default function RequestPart() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [cities, setCities] = useState([]);
  const [vehicle, setVehicle] = useState({ make: sp.get("make") || "", model: sp.get("model") || "", year: "", trim: "" });
  const [partName, setPartName] = useState(sp.get("q") || "");
  const [description, setDescription] = useState("");
  const [city, setCity] = useState("");
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/cities").then(({ data }) => setCities(data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("سجّل الدخول أولًا لإرسال الطلب"); nav("/login"); return; }
    if (user.role !== "customer") { toast.error("طلب القطع متاح لحسابات العملاء فقط"); return; }
    if (!vehicle.make || !vehicle.model) { toast.error("اختر الشركة والموديل"); return; }
    if (!partName.trim()) { toast.error("اكتب اسم القطعة المطلوبة"); return; }
    setLoading(true);
    try {
      await api.post("/part-requests", {
        make: vehicle.make, model: vehicle.model,
        year: vehicle.year ? +vehicle.year : null, trim: vehicle.trim || "",
        part_name: partName.trim(), description, city, images,
      });
      toast.success("تم إرسال طلبك للتشاليح! ستصلك العروض قريبًا.");
      nav("/my-requests");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <div className="text-center mb-8">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--amber)] text-[#0B0F17] mb-3"><PackageSearch size={28} /></span>
        <h1 className="font-head text-2xl font-black text-white">اطلب قطعة</h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">ما لقيت القطعة؟ أرسل طلبك وستصلك عروض من التشاليح لتقارن وتختار الأنسب.</p>
      </div>

      <form onSubmit={submit} className="ts-card p-6 space-y-5" data-testid="request-part-form">
        <div>
          <p className="text-sm font-bold text-white mb-3">بيانات السيارة</p>
          <VehiclePicker value={vehicle} onChange={setVehicle} includeYear includeTrim />
        </div>
        <div>
          <label className="text-xs font-bold text-[var(--text-muted)] block mb-1.5">اسم القطعة المطلوبة *</label>
          <input data-testid="request-part-name" value={partName} onChange={(e) => setPartName(e.target.value)} required
            placeholder="مثال: صدام أمامي، دينمو، طرمبة بنزين" className="w-full rounded-xl bg-[var(--bg-secondary)] px-4 py-3 text-white outline-none" />
        </div>
        <div>
          <label className="text-xs font-bold text-[var(--text-muted)] block mb-1.5">وصف إضافي (اختياري)</label>
          <textarea data-testid="request-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
            placeholder="أي تفاصيل تساعد التشليح على معرفة القطعة بدقة" className="w-full rounded-xl bg-[var(--bg-secondary)] px-4 py-3 text-white outline-none resize-none" />
        </div>
        <div>
          <label className="text-xs font-bold text-[var(--text-muted)] block mb-1.5">المدينة (اختياري)</label>
          <select data-testid="request-city" value={city} onChange={(e) => setCity(e.target.value)}
            className="w-full rounded-xl bg-[var(--bg-secondary)] px-4 py-3 text-white outline-none">
            <option value="">كل المدن</option>
            {cities.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-bold text-[var(--text-muted)] block mb-1.5">صورة للقطعة (اختياري)</label>
          <ImageUploader images={images} setImages={setImages} />
        </div>
        <button data-testid="submit-request-part" disabled={loading} className="btn-amber w-full justify-center disabled:opacity-60">
          {loading ? "جارٍ الإرسال..." : (<><Send size={18} /> إرسال الطلب للتشاليح</>)}
        </button>
      </form>
    </div>
  );
}
